import {
  fetchNotifications,
  markNotificationsRead,
  flagNotifications,
  deleteNotifications,
  getAuthToken,
} from '../../services/api';

// A cache, not a feed: every view (scope + channel + flagged/level/favourites)
// pulls its own last FEED_SIZE once, and live events land on top. A view shows
// the newest FEED_SIZE cached rows matching it, which is exactly its last
// FEED_SIZE. ponytail: the cache only grows within a session — prune if a tab
// left open for days ever measurably bloats.
export const FEED_SIZE = 100;

let state = { items: [], loading: false };
const listeners = new Set();

let ws = null;
let reconnectTimer = null;
let started = false;
const loaded = new Set();
const pending = new Map();
let inflight = 0;

const emit = () => listeners.forEach((l) => l());

const setState = (patch) => {
  state = { ...state, ...patch };
  emit();
};

const upsert = (item) => {
  if (!item || state.items.some((n) => n.id === item.id)) return;
  setState({ items: [item, ...state.items] });
};

const merge = (rows) => {
  const known = new Set(state.items.map((n) => n.id));
  const fresh = (rows || []).filter((n) => !known.has(n.id));
  return fresh.length ? [...state.items, ...fresh] : state.items;
};

// Load a view's last FEED_SIZE once per session. Without a token yet the view
// waits in `pending` for the retry timer.
export const ensure = async (filters = {}) => {
  const key = JSON.stringify(filters);
  if (loaded.has(key)) return;
  if (!getAuthToken()) { pending.set(key, filters); return; }
  loaded.add(key);
  pending.delete(key);
  inflight += 1;
  setState({ loading: true });
  const res = await fetchNotifications({ ...filters, size: FEED_SIZE });
  inflight -= 1;
  setState({ items: merge(res.notifications), loading: inflight > 0 });
};

const connect = () => {
  if (ws || !getAuthToken()) return;
  ws = new WebSocket(`${process.env.REACT_APP_API_WS_BASE_URL}/notifications-socket`);
  ws.onopen = () => ws.send(JSON.stringify({ Authentication: getAuthToken() }));
  ws.onmessage = (e) => {
    try {
      const msg = JSON.parse(e.data);
      if (msg.type === 'notification' && msg.data) upsert(msg.data);
    } catch (err) {
      void err;
    }
  };
  ws.onclose = () => {
    ws = null;
    reconnectTimer = setTimeout(connect, 4000);
  };
  ws.onerror = () => { if (ws) ws.close(); };
};

const start = () => {
  if (started) return;
  started = true;
  connect();
  setInterval(() => {
    if (getAuthToken()) { [...pending.values()].forEach(ensure); connect(); }
  }, 3000);
};

export const subscribe = (listener) => {
  listeners.add(listener);
  start();
  return () => listeners.delete(listener);
};

export const getSnapshot = () => state;

export const markRead = async (ids) => {
  if (!ids || !ids.length) return;
  const s = new Set(ids);
  setState({ items: state.items.map((n) => (s.has(n.id) ? { ...n, is_read: true } : n)) });
  await markNotificationsRead(ids);
};

export const toggleFlag = async (id) => {
  let flagged = true;
  setState({
    items: state.items.map((n) => {
      if (n.id !== id) return n;
      flagged = !n.is_flagged;
      return { ...n, is_flagged: flagged };
    }),
  });
  await flagNotifications([id], flagged);
};

export const remove = async (id) => {
  const result = await deleteNotifications([id]);
  if (result) {
    setState({ items: state.items.filter((n) => n.id !== id) });
  }
};

export const clearScoped = async (ids) => {
  if (!ids?.length) return;
  const result = await deleteNotifications(ids);
  if (result) {
    const s = new Set(ids);
    setState({ items: state.items.filter((n) => !s.has(n.id)) });
  }
};
